import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Catches render errors so one bad component does not blank the whole app.
 *
 * Without a boundary React unmounts the entire tree on any thrown render error
 * and leaves a white page with nothing in it — no message, no navigation, and
 * no indication that a reload might help. That is the worst possible failure
 * mode: the user cannot tell a crash from a hang from being logged out.
 *
 * Must be a class: componentDidCatch has no hook equivalent.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Kept to the console rather than shipped anywhere — there is no error
    // reporting service wired up, and inventing one here would be a surprise.
    if (process.env.NODE_ENV === 'development') {
      console.error('Render error caught by boundary:', error, info)
    }
  }

  componentDidUpdate(prevProps) {
    // Navigating away should clear the error, otherwise the boundary keeps
    // showing a crash for a page the user has already left.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-lg w-full bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-8 text-center">
          <div className="text-5xl mb-4">😵‍💫</div>
          <h1 className="text-2xl font-bold text-white mb-2">This page hit a snag</h1>
          <p className="text-gray-300 mb-6">
            Something broke while rendering. Your data is safe — this is a display
            problem, not a lost event.
          </p>

          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={() => this.setState({ error: null })}
              className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-5 rounded-lg transition-all duration-300"
            >
              Try again
            </button>
            <button
              onClick={() => this.props.onGoHome?.()}
              className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-5 rounded-lg border border-white/20 transition-all duration-300"
            >
              Back to home
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <pre className="mt-6 text-left text-xs text-red-300 bg-black/30 rounded-lg p-3 overflow-auto max-h-48">
              {this.state.error.stack || String(this.state.error)}
            </pre>
          )}
        </div>
      </div>
    )
  }
}

/**
 * Router-aware wrapper: resets the boundary on navigation and gives the
 * fallback a working "home" button. Kept separate because the boundary itself
 * must be a class and classes cannot use hooks.
 */
export const RouteErrorBoundary = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()

  return (
    <ErrorBoundary resetKey={location.pathname} onGoHome={() => navigate('/home')}>
      {children}
    </ErrorBoundary>
  )
}

export default ErrorBoundary
